<template>
  <div class="rdi-wrapper" @contextmenu.prevent="onContextMenu">
    <div
      ref="editor"
      class="rdi-editor"
      :contenteditable="!isDisabled ? 'true' : 'false'"
      :class="{ 'rdi-disabled': isDisabled }"
      @input="onInput"
      @paste="onPaste"
    ></div>

    <!-- Custom context menu -->
    <div
      v-if="menuVisible"
      class="rdi-ctx-menu"
      :style="{ top: menuY + 'px', left: menuX + 'px' }"
      @mousedown.prevent
    >
      <button class="rdi-ctx-item" @click="execCmd('bold')">
        <span class="rdi-ctx-icon"><b>N</b></span>
        <span>Negrita</span>
      </button>
      <button class="rdi-ctx-item" @click="execCmd('italic')">
        <span class="rdi-ctx-icon"><em>I</em></span>
        <span>Cursiva</span>
      </button>
      <button class="rdi-ctx-item" @click="execCmd('underline')">
        <span class="rdi-ctx-icon"><u>S</u></span>
        <span>Subrayado</span>
      </button>
      <div class="rdi-ctx-divider"></div>
      <button
        class="rdi-ctx-item rdi-ctx-clear"
        @click="execCmd('removeFormat')"
      >
        <span class="rdi-ctx-icon">✕</span>
        <span>Quitar formato</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "RichDetailInputComponent",
  props: {
    value: { default: null },
    isDisabled: { default: false }
  },
  data() {
    return {
      menuVisible: false,
      menuX: 0,
      menuY: 0,
      lastEmitted: null
    };
  },
  mounted() {
    if (this.value) {
      this.$refs.editor.innerHTML = this.value;
    }
    document.addEventListener("click", this.closeMenu);
    document.addEventListener("keydown", this.onKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeMenu);
    document.removeEventListener("keydown", this.onKeyDown);
  },
  watch: {
    value(newVal) {
      // Only sync from outside if the change didn't come from our own input
      if (newVal !== this.lastEmitted) {
        this.$refs.editor.innerHTML = newVal || "";
      }
    }
  },
  methods: {
    onInput() {
      let html = this.$refs.editor.innerHTML;
      // Normalize empty editor
      if (html === "<br>" || html === "<div><br></div>") html = "";
      this.lastEmitted = html;
      this.$emit("onInputChange", html);
    },
    onPaste(e) {
      // Descarta el estilo de origen (fuente, color, tamaño, clases) pero
      // conserva estructura: saltos de línea y negrita/cursiva/subrayado.
      // Importante: el resultado debe quedar SOLO con tags de la whitelist
      // que usa el backend al generar PDFs/emails (b/strong/i/em/u/br/p/
      // ul/ol/li/span) — cualquier otro tag (ej. <div>, que produce
      // execCommand("insertText") con texto multilínea) se elimina sin
      // dejar separador, pegando todo el texto.
      e.preventDefault();
      const clipboard = e.clipboardData || window.clipboardData;
      const sourceHtml = clipboard.getData("text/html");
      const htmlToInsert = sourceHtml
        ? this.sanitizePastedHtml(sourceHtml)
        : this.plainTextToHtml(clipboard.getData("text/plain") || "");
      if (htmlToInsert) {
        document.execCommand("insertHTML", false, htmlToInsert);
      }
      this.onInput();
    },
    escapeHtml(str) {
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    },
    plainTextToHtml(text) {
      return text
        .split(/\r\n|\r|\n/)
        .map(line => this.escapeHtml(line))
        .join("<br>");
    },
    sanitizePastedHtml(html) {
      const INLINE_TAGS = new Set(["B", "STRONG", "I", "EM", "U"]);
      const BLOCK_TAGS = new Set([
        "DIV",
        "P",
        "LI",
        "TR",
        "H1",
        "H2",
        "H3",
        "H4",
        "H5",
        "H6"
      ]);
      const container = document.createElement("div");
      container.innerHTML = html;

      // Devuelve un array de líneas (cada una ya saneada); los bloques
      // (div/p/li/etc) delimitan líneas, en vez de perderse al filtrarlos.
      const walk = node => {
        let lines = [""];
        node.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            lines[lines.length - 1] += this.escapeHtml(child.textContent);
            return;
          }
          if (child.nodeType !== Node.ELEMENT_NODE) return;
          const tag = child.tagName;
          if (tag === "BR") {
            lines.push("");
          } else if (tag === "SCRIPT" || tag === "STYLE") {
            // se ignoran por completo
          } else if (INLINE_TAGS.has(tag)) {
            const innerLines = walk(child);
            innerLines.forEach((line, i) => {
              if (i > 0) lines.push("");
              if (line) {
                const t = tag.toLowerCase();
                lines[lines.length - 1] += `<${t}>${line}</${t}>`;
              }
            });
          } else if (BLOCK_TAGS.has(tag)) {
            if (lines[lines.length - 1] !== "") lines.push("");
            const innerLines = walk(child);
            innerLines.forEach((line, i) => {
              if (i > 0) lines.push("");
              lines[lines.length - 1] += line;
            });
            lines.push("");
          } else {
            // Tag no soportado (span, font, a, etc.) — se descarta el tag
            // (y con él su estilo) pero se conserva su contenido.
            const innerLines = walk(child);
            innerLines.forEach((line, i) => {
              if (i > 0) lines.push("");
              lines[lines.length - 1] += line;
            });
          }
        });
        return lines;
      };

      const lines = walk(container);
      while (lines.length > 1 && lines[0] === "") lines.shift();
      while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
      return lines.join("<br>");
    },
    onContextMenu(e) {
      const rect = this.$el.getBoundingClientRect();
      let x = e.clientX - rect.left;
      let y = e.clientY - rect.top;

      // Keep menu inside wrapper horizontally
      const menuWidth = 170;
      if (x + menuWidth > rect.width) {
        x = rect.width - menuWidth;
      }

      this.menuX = Math.max(0, x);
      this.menuY = y + 4;
      this.menuVisible = true;
    },
    closeMenu() {
      this.menuVisible = false;
    },
    onKeyDown(e) {
      if (e.key === "Escape") this.menuVisible = false;
    },
    execCmd(cmd) {
      this.$refs.editor.focus();
      document.execCommand(cmd, false, null);
      this.menuVisible = false;
      this.onInput();
    }
  }
};
</script>

<style lang="scss" scoped>
.rdi-wrapper {
  position: relative;
  width: 100%;
}

.rdi-editor {
  width: 100%;
  min-height: 80px;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  color: #31355c;
  line-height: 1.5;
  outline: none;
  box-sizing: border-box;
  word-break: break-word;
  white-space: pre-wrap;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    border-color: #3ecfb4;
  }

  &.rdi-disabled {
    background-color: #f5f7fa;
    color: #c0c4cc;
    cursor: not-allowed;
  }

  // Preserve formatting in output
  b,
  strong {
    font-weight: 700;
  }
  i,
  em {
    font-style: italic;
  }
  u {
    text-decoration: underline;
  }
}

// Context menu
.rdi-ctx-menu {
  position: absolute;
  z-index: 9999;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(49, 53, 92, 0.14);
  padding: 4px 0;
  min-width: 160px;
  border: 1px solid #eaedf3;
  overflow: hidden;
}

.rdi-ctx-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 14px;
  background: none;
  border: none;
  font-size: 13px;
  color: #31355c;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;

  &:hover {
    background: #f0faf7;
  }

  &.rdi-ctx-clear {
    color: #909399;
    &:hover {
      background: #fdf5f5;
      color: #a33;
    }
  }
}

.rdi-ctx-icon {
  width: 20px;
  text-align: center;
  font-size: 13px;
  flex-shrink: 0;
}

.rdi-ctx-divider {
  height: 1px;
  background: #eaedf3;
  margin: 3px 0;
}
</style>
